import React from "react";
import styles from "./ContactUser.module.scss";
import { Button, ButtonVariant } from "../button/Button";
import { SubHeader } from "@src/pages/home/children/pricing/components/manage-inventory/manage-listing/AdvancedTab/components/SubHeader/SubHeader";
import SpacedLabelValue from "../helpAndSupport/SpacedLabelValue";
import { DateFormatsEnum, formatDate } from "../utils/dateUtils";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SelectYup } from "../select/SelectYup";
import { ContactUserValidation, ContactUserValidationType } from "@src/validation/ContactUserValidation";
import { Textarea } from "../textarea/Textarea";
import { options } from "./options";
import { constructContactUserPayload } from "@src/utils/contactUser.utils";
import { toast } from "sonner";
import { systemActions } from "@src/store";

type TContactUser = {
	selectedRow: EventInventoryRecord | null;
	closeContextMenu: () => void;
};

const ContactUser: React.FC<TContactUser> = ({ selectedRow, closeContextMenu }) => {
	const selectedEvent = useAppSelector((state) => state.pricing.selection.event);
	const formattedEventDate = formatDate(selectedEvent?.Event_Date, DateFormatsEnum["MM/DD/YYYY"]);
	const isLoading = useAppSelector((state) => state.system.contactUser.isLoading);
	const { nameId, companyName } = useAppSelector((state) => state.settings);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(ContactUserValidation),
	});

	const subjectError = errors?.subject?.message;
	const dispatch = useAppDispatch();

	const onSubmit: SubmitHandler<ContactUserValidationType> = async (data) => {
		if (!selectedEvent || !selectedRow || !companyName) {
			toast.warning("Necessary data unavailable");
			return;
		}

		const payload = constructContactUserPayload({
			subject: data.subject,
			message: data.message,
			selectedEvent,
			selectedRow,
			nameId,
			companyName,
		});

		try {
			await dispatch(systemActions.createContactUserEmail(payload)).unwrap();
			toast.success("Email sent to the listing owner.");
			closeContextMenu();
		} catch {
			toast.warning("Error while sending the email.");
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.root}>
			<div className={styles.content}>
				<div className={styles.listing}>
					<SubHeader title="Selected Listing" tooltipText="Selected listing and event information" />
					<div className={styles.listingsContent}>
						<SpacedLabelValue label="Event:" value={selectedEvent?.Event_Headliner} />
						<SpacedLabelValue label="Date:" value={formattedEventDate} />
						<SpacedLabelValue label="Time:" value={selectedEvent?.Event_Time} />
						<SpacedLabelValue label="Venue:" value={selectedEvent?.Venue_Name} />
						<SpacedLabelValue label="Section:" value={selectedRow?.Section} />
						<SpacedLabelValue label="Row:" value={selectedRow?.Row} />
						<SpacedLabelValue label="Seats:" value={selectedRow?.Seats} />
						<SpacedLabelValue label="Listing ID:" value={selectedRow?.Listing_ID} />
					</div>
				</div>
				<div className={styles.description}>
					<SubHeader title="Email" tooltipText="Compose the email to the listing owner" />
					<div className={styles.descriptionContent}>
						<div className={styles.headline}>Subject</div>
						<div className={styles.subject}>
							<SelectYup control={control} name="subject" options={options} />
							{subjectError ? <div className={styles.subjectError}>{subjectError}</div> : null}
						</div>
						<div className={styles.headline}>Message (Optional)</div>
						<Textarea control={control} name="message" placeholder="Type here" />
					</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button disabled={isLoading} onClick={closeContextMenu} variant={ButtonVariant.Secondary}>
					Cancel
				</Button>
				<Button disabled={isLoading} isLoading={isLoading} type="submit" variant={ButtonVariant.Primary}>
					Submit
				</Button>
			</div>
		</form>
	);
};

export default ContactUser;
